import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal } from '@snsw/react-component-library';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearApplication, saveApplication, storeCurrentPath } from 'shared/redux/applicationSlice';
import { clearPlantDesignForm, storeRedirect } from 'plantItemNew/redux/slices/formSlice';
import { store } from 'shared/redux/configureStore';
import { UserPasswordlessManager } from 'shared/oidc/passwordLessUserManager';
import { PagePath } from 'shared/constants/pagePath';
import { getApplicationRootPath } from 'shared/utils/NavigationHelper';

export const IdleTimer = ({ timeout, promptTimeout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const passwordlessOidc = useSelector((state) => state.passwordlessOidc);
  const application = useSelector((state) => state.application);
  const form = useSelector((state) => state.form);

  const [aboutToTimedOut, setAboutToTimedOut] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const intervalId = useRef(null);
  const formatRemainingTime = (remainingTime) => {
    return new Date(remainingTime).toISOString().substring(14, 19).replace(':', '.');
  };

  const [remaining, setRemaining] = useState('');

  const tick = useCallback(() => {
    setRemaining(formatRemainingTime(idleTimer.getRemainingTime()));
  }, [timeout, promptTimeout, aboutToTimedOut]);

  const onPrompt = () => {
    if (intervalId.current) clearInterval(intervalId.current);

    intervalId.current = setInterval(tick, 1000);
    setAboutToTimedOut(true);
  };

  const onIdle = () => {
    setAboutToTimedOut(false);
    setTimedOut(true);
    clearData();
  };

  const onContinue = () => {
    clearInterval(intervalId.current);
    idleTimer.reset();
    setAboutToTimedOut(false);
    setRemaining('');
  };

  const onBackToSNSW = () => {
    // TODO: Redirect to SNSW
    clearData();
  };

  const clearData = () => {
    let applicationPathParam = getApplicationRootPath(location.pathname);

    if (application.applicationId && application.licenceType !== PagePath.plantItemRenewalRoot) {
      applicationPathParam += `/application/${application.applicationId}`;
    }

    const redirectUrl = `${PagePath.timeout}?applicationpath=${encodeURIComponent(applicationPathParam)}`;

    store.dispatch(storeCurrentPath(location.pathname));

    store.dispatch(saveApplication(false)).then(() => {
      UserPasswordlessManager.instance().stopSilentRenew();

      store.dispatch(clearPlantDesignForm());
      store.dispatch(storeRedirect(redirectUrl));
      store.dispatch(clearApplication());
      setTimedOut(true);
      UserPasswordlessManager.instance().signoutRedirect();
    });
  };

  const onTimedOutClose = () => {
    setTimedOut(false);
    navigate(form.redirectTo);
  };

  const idleTimer = useIdleTimer({
    timeout,
    promptTimeout,
    onPrompt,
    onIdle,
    startOnMount: false,
    startManually: true,
    stopOnIdle: true,
  });

  useEffect(() => {
    if (passwordlessOidc.user && !passwordlessOidc.user.expired) {
      idleTimer.start();
    } else {
      idleTimer.reset();
      idleTimer.pause();
    }
  }, [passwordlessOidc]);

  return (
    <>
      {aboutToTimedOut && (
        <Modal
          title="Your session is about to timed out"
          buttons={[
            { text: 'Back to Service NSW', onClick: onBackToSNSW },
            { text: 'Continue registration', onClick: onContinue },
          ]}
        >
          Your session is about to timeout in <b>{remaining}</b>.
          <br />
          For your security, you will be logged out.
        </Modal>
      )}
      {timedOut && (
        <Modal
          title="Your session has timed out"
          buttons={[{ text: 'Continue registration', onClick: onTimedOutClose }]}
        >
          There has been no activity for the last {Math.ceil((timeout + promptTimeout) / (1000 * 60))} minutes.
          <br />
          For your security, you have been logged out.
        </Modal>
      )}
    </>
  );
};

IdleTimer.propTypes = {
  promptTimeout: PropTypes.number.isRequired,
  timeout: PropTypes.number.isRequired,
};
